import React from "react";
import { X } from "lucide-react";
import { getTeamStyle } from "../utils/teamColors";
import JerseyNumber from "../utils/JerseyNumber";

const playoffRoundLabel = (round) => {
  switch (round) {
    case 1:
      return "Round 1";
    case 2:
      return "Quarterfinals";
    case 3:
      return "Semifinals";
    case 4:
      return "Finals";
    default:
      return "Playoff";
  }
};

const PlayerStatsModal = ({ player, gameStats, onClose, modalLoading }) => {
  const teamObj = {
    primary_color: player.team_primary_color,
    secondary_color: player.team_secondary_color,
    gradient: player.team_gradient,
  };

  const ts = getTeamStyle(teamObj);
  const headerText = (() => {
    const hex = (player.team_primary_color || "1f2937").replace("#", "");
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 128 ? "#000000" : "#ffffff";
  })();

  // Sort games by date (newest first), then filter out DNP games
  const allGames = [...gameStats].sort(
    (a, b) => new Date(b.game.game_date) - new Date(a.game.game_date),
  );

  // ── DNP Filter: Exclude games where all stats are zero ──
  const sortedGames = allGames.filter((row) => {
    const isDNP =
      row.points === 0 &&
      row.rebounds === 0 &&
      row.assists === 0 &&
      row.three_pm === 0 &&
      row.two_pm === 0 &&
      row.ft_made === 0 &&
      (row.personal_fouls ?? 0) === 0 &&
      (row.technical_fouls ?? 0) === 0;
    return !isDNP;
  });

  const totalGames = sortedGames.length;
  const hasGames = totalGames > 0;

  const sumStat = (key) =>
    sortedGames.reduce((acc, r) => acc + (r[key] ?? 0), 0);
  const perGame = (key) =>
    hasGames ? (sumStat(key) / totalGames).toFixed(1) : "0.0";

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-2xl max-w-4xl w-full max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div
          className="px-8 py-6 relative"
          style={{ ...ts.headerStyle, color: headerText }}
        >
          <button
            onClick={onClose}
            className="absolute top-6 right-6 p-2 hover:bg-white/20 rounded-full transition"
          >
            <X size={28} />
          </button>

          <div className="flex items-center gap-4">
            <div>
              <div className="flex items-center gap-3">
                <JerseyNumber
                  number={player.jersey}
                  color={headerText}
                  size="xl"
                  mode="inline"
                  bgColor={ts.primaryHex}
                />
                <h2
                  className="text-3xl font-bold tracking-tight"
                  style={{ color: headerText }}
                >
                  {player.name}
                </h2>
              </div>
              <p
                className="text-xl opacity-90 mt-1"
                style={{ color: headerText }}
              >
                {player.team}
              </p>
            </div>
          </div>

          {/* Quick summary */}
          <div className="flex gap-8 mt-6 text-sm">
            <div>
              <span className="opacity-75">GAMES</span>
              <div className="text-3xl font-black tabular-nums">
                {totalGames}
              </div>
            </div>
            <div>
              <span className="opacity-75">PPG</span>
              <div className="text-3xl font-black tabular-nums">
                {perGame("points")}
              </div>
            </div>
            <div>
              <span className="opacity-75">RPG</span>
              <div className="text-3xl font-black tabular-nums">
                {perGame("rebounds")}
              </div>
            </div>
            <div>
              <span className="opacity-75">APG</span>
              <div className="text-3xl font-black tabular-nums">
                {perGame("assists")}
              </div>
            </div>
          </div>
        </div>

        {/* Game-by-Game Table */}
        <div className="flex-1 overflow-auto p-6">
          {modalLoading ? (
            <div className="text-center py-12 dark:text-white">
              Loading game history...
            </div>
          ) : totalGames === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              No game data available yet.
            </div>
          ) : (
            <table className="w-full">
              <thead className="sticky top-0 bg-gray-100 dark:bg-gray-800 z-10">
                <tr>
                  <th className="text-left p-3 font-semibold text-gray-600 dark:text-gray-300">
                    Date
                  </th>
                  <th className="text-left p-3 font-semibold text-gray-600 dark:text-gray-300">
                    Opponent
                  </th>
                  <th className="text-center p-3 font-semibold text-gray-600 dark:text-gray-300">
                    PTS
                  </th>
                  <th className="text-center p-3 font-semibold text-gray-600 dark:text-gray-300">
                    REB
                  </th>
                  <th className="text-center p-3 font-semibold text-gray-600 dark:text-gray-300">
                    AST
                  </th>
                  <th className="text-center p-3 font-semibold text-gray-600 dark:text-gray-300">
                    3PM
                  </th>
                  <th className="text-center p-3 font-semibold text-gray-600 dark:text-gray-300">
                    2PM
                  </th>
                  <th className="text-center p-3 font-semibold text-gray-600 dark:text-gray-300">
                    FTM
                  </th>
                  <th className="text-center p-3 font-semibold text-gray-600 dark:text-gray-300">
                    PF
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {sortedGames.map((row, i) => {
                  const g = row.game;
                  const isHome = g.team1_id === player.team_id;
                  const opponent = isHome ? g.team2?.name : g.team1?.name;
                  const opponentPrefix = "vs"; //const opponentPrefix = isHome ? 'vs' : '@'; used for at home games/away games
                  const myScore = isHome ? g.team1_score : g.team2_score;
                  const oppScore = isHome ? g.team2_score : g.team1_score;
                  const won = myScore > oppScore;

                  return (
                    <tr
                      key={i}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition"
                    >
                      <td className="p-3 whitespace-nowrap text-gray-700 dark:text-gray-300">
                        {new Date(g.game_date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        })}
                      </td>
                      <td className="p-3 font-medium text-gray-800 dark:text-gray-200">
                        <span
                          className={`font-bold mr-1.5 ${
                            won
                              ? "text-green-600 dark:text-green-400"
                              : "text-red-600 dark:text-red-400"
                          }`}
                        >
                          {won ? "W" : "L"}
                        </span>
                        <span className="text-gray-400">{opponentPrefix}</span>{" "}
                        {opponent}
                        {g.game_type === "playoff" && (
                          <span className="ml-2 text-[9px] font-bold px-1.5 py-0.5 rounded bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 uppercase tracking-wide">
                            🏆 {playoffRoundLabel(g.playoff_round)}
                          </span>
                        )}
                      </td>
                      <td className="text-center p-3 font-bold tabular-nums text-orange-600 dark:text-orange-400">
                        {row.points}
                      </td>
                      <td className="text-center p-3 tabular-nums dark:text-white">
                        {row.rebounds}
                      </td>
                      <td className="text-center p-3 tabular-nums dark:text-white">
                        {row.assists}
                      </td>
                      <td className="text-center p-3 tabular-nums dark:text-white">
                        {row.three_pm}
                      </td>
                      <td className="text-center p-3 tabular-nums dark:text-white">
                        {row.two_pm}
                      </td>
                      <td className="text-center p-3 tabular-nums dark:text-white">
                        {row.ft_made}
                      </td>
                      <td className="text-center p-3 tabular-nums text-orange-900 dark:text-orange-300">
                        {row.personal_fouls}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="border-t dark:border-gray-700 px-8 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-2xl font-semibold transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlayerStatsModal;
